/**
 * Processing Service — App Shell path
 *
 * Submits text for AI processing via the v3 endpoint.
 * Returns a result object instead of calling setter callbacks.
 * No WebSocket / clientId — backend writes to Firestore, frontend
 * picks up changes via useProcessingListener (onSnapshot).
 *
 * See: docs/features/integrations/deepgram-prerecorded.md
 */
import { auth } from "./firebase";
import { updateData } from "./firestoreService";
import { minimumTextLength, ServerURI } from "../globals";
import { optimizePatientHistory, withRetry } from "./documentCreationUtils";
import { showNotification } from "./notificationSystem";
import { getAuthHeaders } from "./apiUtils";

/**
 * Submit text for AI processing.
 *
 * @param {Object} params
 * @param {string} params.text           — Raw transcript or pasted text
 * @param {string|number} params.documentTypeId — Selected document type
 * @param {Object} params.patientData    — Patient record
 * @param {Array}  params.patientDocuments — Patient document history
 * @param {string} params.documentId     — Firestore document ID
 * @param {string} params.patientId      — Patient ID
 * @param {string} [params.streamSessionId] — Streaming session ID
 * @param {string} [params.source='streaming'] — 'streaming' | 'recording' | 'upload' | 'text' | 'summary' | 'export'
 * @returns {Promise<{success: boolean, status: string, jobId?: string, error?: string}>}
 */
export async function submitForProcessing({
  text,
  documentTypeId,
  patientData,
  patientDocuments,
  documentId,
  patientId,
  streamSessionId = null,
  source = "streaming",
}) {
  try {
    // Validate text
    if (!text || text.trim().length === 0) {
      return { success: false, status: "no_text", error: "No text to process" };
    }

    // Streaming = 50 (raw partial transcripts).
    // Summary/export = 50 (sources are already-processed clinical fields, can be terse).
    // Recording/upload/text = full minimum, including finalized audio transcripts.
    const effectiveMinLength =
      source === "streaming" || source === "summary" || source === "export" ? 50 : minimumTextLength;

    if (text.length < effectiveMinLength) {
      showNotification(
        `Text too short for processing. Minimum ${effectiveMinLength} characters required.`,
        "warning",
        8000,
        { key: "text-too-short" }
      );
      return { success: false, status: "too_short", error: "Text too short for processing" };
    }

    const optimizedHistory = optimizePatientHistory(patientDocuments || []);
    const userId = auth.currentUser?.uid;

    // Map our internal `source` flag to the backend `sourceType` label.
    // Summary generation inlines source docs in `text`, so we don't also send
    // `patientHistory` (sources would duplicate). See summary-generation.md.
    const sourceTypeMap = {
      streaming: "Recording",
      recording: "Recording",
      upload: "Upload",
      text: "Upload",
      summary: "summary",
      export: "export",
    };
    const isSummary = source === "summary" || source === "export";

    const requestPayload = {
      text: text.trim(),
      docTypeId: documentTypeId,
      patientData,
      patientHistory: isSummary ? null : optimizedHistory,
      documentId,
      patientId,
      userId,
      sourceType: sourceTypeMap[source] || "Recording",
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    console.log("[processingService] Submitting to v3:", {
      textLength: text.length,
      docTypeId: documentTypeId,
      patientId,
      docId: documentId,
      userId,
      source,
    });

    const headers = await getAuthHeaders();

    const response = await withRetry(
      () =>
        fetch(`${ServerURI}/api/v3/processText`, {
          method: "POST",
          headers,
          body: JSON.stringify(requestPayload),
        }),
      3,
      (retryCount, maxRetries, error) => {
        console.log(`[processingService] Retry ${retryCount}/${maxRetries}: ${error.message}`);
      }
    );

    if (response.ok) {
      const result = await response.json();
      if (result.status === "queued") {
        console.log(`[processingService] Job queued: ${result.jobId}`);
        return { success: true, status: "queued", jobId: result.jobId };
      }
      return { success: true, status: "completed" };
    }

    if (response.status === 409) {
      return { success: false, status: "conflict", error: "Document already being processed" };
    }

    if (response.status === 422) {
      // Structured prerequisite failure — surface the specific unmet rule(s)
      // instead of a generic "Server error 422" toast.
      const errorData = await response.json().catch(() => ({}));

      if (["HISTORY_SOURCE_UNAVAILABLE", "HISTORY_CONTEXT_TOO_LARGE"].includes(errorData.code)) {
        if (documentId && patientId) {
          try {
            await updateData(`patients/${patientId}/documents`, documentId, {
              status: "error", isArchived: true, modifiedAt: new Date(),
            });
          } catch (cleanupError) {
            console.error("[processingService] Failed to archive rejected history placeholder:", cleanupError);
          }
        }
        return { success: false, status: "history_unavailable", error: errorData.error };
      }

      if (errorData.code === "PREREQUISITES_NOT_MET" && Array.isArray(errorData.unmet)) {
        const lines = errorData.unmet.map((u) => {
          const name = u.requiredSubcategoryName || u.requiredSubcategory;
          return `Requires ${u.minCount} ${name} (currently ${u.currentCount ?? 0}).`;
        });
        const message = lines.join(" ");

        showNotification(message, "warning", 10000, {
          key: "prereqs-not-met",
          priority: true,
        });

        // Hide the placeholder doc so the patient page doesn't show a stuck
        // error card. This is a fallback for a server-side race or stale client;
        // the summary flow normally catches prerequisite gaps before creation.
        if (documentId && patientId) {
          try {
            await updateData(`patients/${patientId}/documents`, documentId, {
              status: "error",
              isArchived: true,
              modifiedAt: new Date(),
            });
          } catch (cleanupError) {
            // Preserve the useful prerequisite response even if cleanup fails.
            console.error("[processingService] Failed to archive rejected placeholder:", cleanupError);
          }
        }

        return {
          success: false,
          status: "prerequisites_not_met",
          error: message,
          unmet: errorData.unmet,
        };
      }

      // Fall through to generic handling if the 422 isn't the shape we expect
      console.error("[processingService] Unexpected 422 shape:", errorData);
    }

    if (response.status === 429) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        status: "rate_limited",
        error: errorData.error || "Too many active jobs",
      };
    }

    // Other HTTP errors
    const errorData = await response.text();
    console.error("[processingService] Server error:", response.status, errorData);

    if (documentId && patientId) {
      await updateData(`patients/${patientId}/documents`, documentId, {
        status: "error",
        modifiedAt: new Date(),
      });
    }

    return { success: false, status: "server_error", error: `Server error ${response.status}` };
  } catch (error) {
    console.error("[processingService] Exception:", error);

    if (documentId && patientId) {
      await updateData(`patients/${patientId}/documents`, documentId, {
        status: "error",
        modifiedAt: new Date(),
      });
    }

    return { success: false, status: "exception", error: error.message };
  }
}

/**
 * Reprocess an entire document using the v3 endpoint.
 *
 * @param {Object} params
 * @param {string} params.documentId — Firestore document ID
 * @param {string} params.patientId  — Patient ID
 * @param {string} [params.mode='full'] — 'full' (clear sections) or 'resume' (keep completed)
 * @returns {Promise<{success: boolean, status: string, jobId?: string, error?: string}>}
 */
export async function reprocessDocument({ documentId, patientId, mode = "full" }) {
  try {
    if (!documentId || !patientId) {
      return { success: false, status: "invalid_params", error: "Missing documentId or patientId" };
    }

    console.log("[processingService] Reprocessing document:", { documentId, patientId, mode });

    const headers = await getAuthHeaders();

    const response = await withRetry(
      () =>
        fetch(`${ServerURI}/api/v3/documents/${documentId}/reprocess`, {
          method: "POST",
          headers,
          body: JSON.stringify({
            patientId,
            mode,
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          }),
        }),
      3,
      (retryCount, maxRetries, error) => {
        console.log(`[reprocessDocument] Retry ${retryCount}/${maxRetries}: ${error.message}`);
      }
    );

    if (response.ok) {
      const result = await response.json();
      console.log(`[processingService] Reprocess queued: ${result.jobId}`);
      return { success: true, status: "queued", jobId: result.jobId, mode: result.mode };
    }

    if (response.status === 400) {
      const errorData = await response.json().catch(() => ({}));
      return { success: false, status: "bad_request", error: errorData.error || "Invalid request" };
    }

    if (response.status === 403) {
      return { success: false, status: "forbidden", error: "Not authorized to reprocess this document" };
    }

    if (response.status === 404) {
      return { success: false, status: "not_found", error: "Document not found" };
    }

    if (response.status === 409) {
      const errorData = await response.json().catch(() => ({}));
      return { success: false, status: "conflict", error: errorData.error || "Document already processing" };
    }

    if (response.status === 429) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        status: "rate_limited",
        error: errorData.error || "Too many active jobs",
      };
    }

    const errorData = await response.text();
    console.error("[reprocessDocument] Server error:", response.status, errorData);
    return { success: false, status: "server_error", error: `Server error ${response.status}` };
  } catch (error) {
    console.error("[reprocessDocument] Exception:", error);
    return { success: false, status: "exception", error: error.message };
  }
}

/**
 * Reprocess a single section using the v3 endpoint.
 *
 * @param {Object} params
 * @param {string} params.documentId — Firestore document ID
 * @param {string} params.patientId  — Patient ID
 * @param {string} params.sectionId  — Section ID (stepId, name, or id)
 * @returns {Promise<{success: boolean, status: string, jobId?: string, sectionId?: string, error?: string}>}
 */
export async function reprocessSection({ documentId, patientId, sectionId }) {
  try {
    if (!documentId || !patientId || !sectionId) {
      return { success: false, status: "invalid_params", error: "Missing required parameters" };
    }

    console.log("[processingService] Reprocessing section:", { documentId, patientId, sectionId });

    const headers = await getAuthHeaders();

    const response = await withRetry(
      () =>
        fetch(`${ServerURI}/api/v3/documents/${documentId}/sections/${sectionId}/reprocess`, {
          method: "POST",
          headers,
          body: JSON.stringify({
            patientId,
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          }),
        }),
      3,
      (retryCount, maxRetries, error) => {
        console.log(`[reprocessSection] Retry ${retryCount}/${maxRetries}: ${error.message}`);
      }
    );

    if (response.ok) {
      const result = await response.json();
      console.log(`[processingService] Section reprocess queued: ${result.jobId}`);
      return {
        success: true,
        status: "queued",
        jobId: result.jobId,
        sectionId: result.sectionId,
        sectionName: result.sectionName,
      };
    }

    if (response.status === 400) {
      const errorData = await response.json().catch(() => ({}));
      return { success: false, status: "bad_request", error: errorData.error || "Invalid request" };
    }

    if (response.status === 403) {
      return { success: false, status: "forbidden", error: "Not authorized" };
    }

    if (response.status === 404) {
      const errorData = await response.json().catch(() => ({}));
      return { success: false, status: "not_found", error: errorData.error || "Section not found" };
    }

    if (response.status === 409) {
      const errorData = await response.json().catch(() => ({}));
      return { success: false, status: "conflict", error: errorData.error || "Document processing" };
    }

    if (response.status === 429) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        status: "rate_limited",
        error: errorData.error || "Too many active jobs",
      };
    }

    const errorData = await response.text();
    console.error("[reprocessSection] Server error:", response.status, errorData);
    return { success: false, status: "server_error", error: `Server error ${response.status}` };
  } catch (error) {
    console.error("[reprocessSection] Exception:", error);
    return { success: false, status: "exception", error: error.message };
  }
}

/**
 * Enqueue a post-export Patient History Summary job.
 *
 * Fire-and-forget: called from the Export Note flow when the `advancedHistory`
 * flag is on. The backend writes a pending job to a delayed lane (coalescing
 * rapid re-exports per patient); a sweeper promotes it ~10 min later. Failures
 * are non-fatal — they must never block or disrupt the export itself.
 *
 * @param {Object} params
 * @param {string} params.patientId
 * @param {string} [params.triggeredByDocId] — the document being exported
 * @param {'export'|'manual'} [params.generatedBy='export']
 * @param {boolean} [params.immediate=false] — run now (manual Update History)
 * @returns {Promise<{success: boolean, coalesced?: boolean, error?: string}>}
 */
export async function enqueueHistorySummary({
  patientId,
  triggeredByDocId = null,
  generatedBy = "export",
  immediate = false,
}) {
  try {
    if (!patientId) {
      return { success: false, error: "patientId required" };
    }

    const headers = await getAuthHeaders();
    const response = await fetch(`${ServerURI}/api/v3/history-summary/enqueue`, {
      method: "POST",
      headers,
      body: JSON.stringify({ patientId, triggeredByDocId, generatedBy, immediate }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.warn(
        `[enqueueHistorySummary] ${response.status}: ${errorData.error || "request failed"}`
      );
      return { success: false, error: errorData.error || `Server error ${response.status}` };
    }

    const result = await response.json();
    return { success: true, coalesced: result.coalesced === true };
  } catch (error) {
    // Non-fatal — log and move on so the export flow is never affected.
    console.warn("[enqueueHistorySummary] Exception:", error.message);
    return { success: false, error: error.message };
  }
}
